import { ComponentProps, useState, useMemo } from 'react';
import { useDate } from '../../contexts/date-context';
import { useAppData } from '../../contexts/app-data-context';
import { Button } from '../ui/button';
import { FormField, Label } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { useToast } from '../ui/toast';
import { categoryTypes, type CategoryType } from '../../types';
import { formatCurrency } from '../../lib/format';

export interface BudgetFormData {
  month: number;
  year: number;
  needs: number;
  comfort: number;
  goals: number;
  education: number;
}

export interface BudgetFormProps extends Omit<
  ComponentProps<'form'>,
  'onSubmit'
> {
  onClose?: () => void;
}

export function BudgetForm({ onClose, ...props }: BudgetFormProps) {
  const { month, year } = useDate();
  const { totalMoney, saveBudget, budget, originalBudget } = useAppData();
  const { showToast } = useToast();

  const [submitting, setSubmitting] = useState(false);

  // Convert percentages to currency values
  const initialValues = useMemo(
    () => ({
      needs: budget.needs,
      comfort: budget.comfort,
      education: budget.education,
      goals: budget.goals,
      freedom: budget.freedom,
    }),
    [budget]
  );

  const [values, setValues] = useState(initialValues);

  const freedom = Math.round((totalMoney - values.needs - values.comfort) * 100) / 100;
  const hasChanged =
    values.needs !== initialValues.needs ||
    values.comfort !== initialValues.comfort;

  const applyRecommended = () => {
    setValues((prev) => ({
      ...prev,
      needs: (75 / 100) * totalMoney,
      comfort: (25 / 100) * totalMoney,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitting(true);

    try {
      await saveBudget({
        month,
        year,
        needs: values.needs,
        comfort: values.comfort,
        education: 0,
        goals: 0,
        freedom: Math.max(0, freedom),
      });
      showToast('Orçamento salvo com sucesso!', 'success');
      onClose?.();
    } catch (error) {
      console.error('Erro ao salvar orçamento:', error);
      showToast('Erro ao salvar orçamento', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const updateValue = (category: CategoryType, value: number) => {
    setValues((prev) => ({ ...prev, [category]: value }));
  };

  return (
    <form
      data-slot="budget-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      {...props}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <Label>Distribuição do orçamento</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applyRecommended}
            title="Aplicar proporção recomendada"
            className="text-primary"
          >
            Recomendado: 75/25
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {(['needs', 'comfort'] as const).map((categoryKey) => {
            const category = categoryTypes[categoryKey];
            return (
              <FormField key={categoryKey}>
                <div className="flex items-center gap-2">
                  <span
                    className="size-3 shrink-0 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <Label htmlFor={categoryKey}>{category.label}</Label>
                </div>
                <CurrencyInput
                  id={categoryKey}
                  value={values[categoryKey]}
                  onChange={(value) => updateValue(categoryKey, value)}
                />
              </FormField>
            );
          })}
        </div>

        <FormField>
          <div className="flex items-center gap-2">
            <span
              className="size-3 shrink-0 rounded-full"
              style={{ backgroundColor: categoryTypes.freedom.color }}
            />
            <Label>{categoryTypes.freedom.label}</Label>
            <span className="text-xs text-muted-foreground">(automático)</span>
          </div>
          <div className={`flex items-center justify-between rounded-lg border px-3 py-2 ${freedom < 0 ? 'border-destructive bg-destructive/5' : 'border-border bg-muted/30'}`}>
            <span className={`text-sm font-semibold ${freedom < 0 ? 'text-destructive' : 'text-foreground'}`}>
              R$ {formatCurrency(Math.max(0, freedom))}
            </span>
            {freedom < 0 && (
              <span className="text-xs text-destructive">
                R$ {formatCurrency(Math.abs(freedom))} excedido
              </span>
            )}
          </div>
        </FormField>
      </div>

      <div className="flex gap-2">
        {onClose && (
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            Cancelar
          </Button>
        )}
        <Button
          disabled={submitting || (!hasChanged && originalBudget !== null)}
          type="submit"
          className="flex-1"
        >
          Salvar orçamento
        </Button>
      </div>
    </form>
  );
}
